"use client";

import React, { useMemo } from "react";
import { Lock, MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  useTicketUpdates,
  authorDisplayName,
  type TicketUpdateRow,
} from "@/features/tickets/hooks/ticket-updates-queries";
import { QuickTicketUpdateAction } from "./QuickTicketUpdateAction";

interface TicketUpdatesListProps {
  ticketId: string;
  ticketNumber?: string;
}

/** Badge styling per update_type — keeps the list scannable. */
const UPDATE_TYPE_STYLES: Record<string, { label: string; className: string }> = {
  comment: { label: "Comment", className: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  status_change: { label: "Status change", className: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  priority_change: { label: "Priority change", className: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300" },
  assignment_change: { label: "Assignment", className: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300" },
  assignment: { label: "Assignment", className: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300" },
  resolution: { label: "Resolution", className: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" },
  customer_communication: { label: "Customer comm", className: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300" },
  internal_note: { label: "Internal note", className: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
  email_reply: { label: "Email reply", className: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300" },
  note: { label: "Note", className: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
};

function updateTypeBadge(updateType: string) {
  const style = UPDATE_TYPE_STYLES[updateType] ?? {
    label: updateType.replace(/_/g, " "),
    className: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
  };
  return <Badge className={`${style.className} border-transparent`}>{style.label}</Badge>;
}

function dayKey(iso: string | null): string {
  if (!iso) return "Unknown date";
  const d = new Date(iso);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" })
    : "Unknown date";
}

function timeOfDay(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isFinite(d.getTime())
    ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : "";
}

/**
 * Organized, date-grouped list of a ticket's `ticket_updates` with type badges,
 * internal flags, and author attribution — plus the Add Update button.
 */
export function TicketUpdatesList({ ticketId, ticketNumber }: TicketUpdatesListProps) {
  const { data: updates, isLoading, error } = useTicketUpdates(ticketId);

  const groups = useMemo(() => {
    const byDay = new Map<string, TicketUpdateRow[]>();
    for (const update of updates ?? []) {
      const key = dayKey(update.created_at);
      const list = byDay.get(key);
      if (list) {
        list.push(update);
      } else {
        byDay.set(key, [update]);
      }
    }
    return Array.from(byDay.entries());
  }, [updates]);

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-lg font-semibold">Ticket Updates</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Work log and comments, newest first
          </div>
        </div>
        <QuickTicketUpdateAction ticketId={ticketId} ticketNumber={ticketNumber} />
      </div>

      {isLoading && (
        <div className="text-sm text-gray-500">Loading updates…</div>
      )}

      {error && !isLoading && (
        <div className="text-sm text-red-600 dark:text-red-400">
          Failed to load updates: {error instanceof Error ? error.message : "unknown error"}
        </div>
      )}

      {!isLoading && !error && (updates?.length ?? 0) === 0 && (
        <div className="flex flex-col items-center gap-2 py-8 text-center text-gray-500 dark:text-gray-400">
          <MessageSquare className="h-8 w-8 opacity-40" />
          <p className="text-sm">No updates yet — add the first one as work begins.</p>
        </div>
      )}

      <div className="space-y-6">
        {groups.map(([day, dayUpdates]) => (
          <div key={day} className="space-y-3">
            <div className="sticky top-0 z-raised bg-background/95 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {day}
            </div>
            <div className="space-y-3 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
              {dayUpdates.map((update) => (
                <div
                  key={update.id}
                  className="rounded-md border border-gray-200 dark:border-gray-700 bg-card p-3 space-y-2"
                >
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    {updateTypeBadge(update.update_type)}
                    {update.is_internal && (
                      <Badge className="bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-transparent gap-1">
                        <Lock className="h-3 w-3" />
                        Internal
                      </Badge>
                    )}
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {authorDisplayName(update.author)}
                    </span>
                    <span>·</span>
                    <span>{timeOfDay(update.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                    {update.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
